import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface PdfPageParams {
  currentPage: number;
  totalPages: number;
}

/**
 * Hook to extract PDF page parameters from query string
 * Supports query parameters: currentPage and totalPages
 * Falls back to default values if parameters are not provided or invalid
 */
export const usePdfPageParams = (defaultCurrentPage = 1, defaultTotalPages = 1): PdfPageParams => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const currentPageParam = searchParams.get('currentPage');
    const totalPagesParam = searchParams.get('totalPages');

    const currentPage = currentPageParam && !Number.isNaN(Number(currentPageParam)) 
      ? Number(currentPageParam) 
      : defaultCurrentPage;

    const totalPages = totalPagesParam && !Number.isNaN(Number(totalPagesParam)) 
      ? Number(totalPagesParam) 
      : defaultTotalPages;

    // Ensure currentPage is within valid range
    const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    return {
      currentPage: validCurrentPage,
      totalPages: Math.max(1, totalPages)
    };
  }, [searchParams, defaultCurrentPage, defaultTotalPages]);
};
